import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { LOAD_STALL_MS } from "@/lib/with-timeout";

// Non-standard NetworkInformation API — present on Chrome/Android (our dominant
// browser in this issue), absent on Safari. Read defensively.
interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

function getConnection(): NetworkInformation | undefined {
  return (navigator as Navigator & { connection?: NetworkInformation })
    .connection;
}

/**
 * Full-screen "taking longer than expected" recovery screen. Shown by
 * GlobalPending when a navigation stays pending past LOAD_STALL_MS — which
 * covers a hung auth getSession as well as any other stalled load. The Reload
 * button gets a fresh document (and fresh Web Locks), which is what actually
 * recovers from a wedged auth lock.
 *
 * Reports to Sentry on mount — i.e. every time a user actually sees this screen —
 * with network/visibility context so we can root-cause and prevent recurrence.
 * (When the cause is the auth hang, this co-occurs with the "auth-getSession-slow"
 * warning emitted by the _authenticated guard at 8s.)
 */
export function LoadTimeout() {
  const { t } = useTranslation();

  useEffect(() => {
    if (!import.meta.env.VITE_SENTRY_DSN) return;
    const conn = getConnection();
    Sentry.captureMessage("app-load-stalled", {
      level: "error",
      tags: { feature: "app-load" },
      extra: {
        thresholdMs: LOAD_STALL_MS,
        pathname: window.location.pathname,
        online: navigator.onLine,
        visibility: document.visibilityState,
        prerendering: (document as Document & { prerendering?: boolean })
          .prerendering,
        effectiveType: conn?.effectiveType,
        downlink: conn?.downlink,
        rtt: conn?.rtt,
        saveData: conn?.saveData,
        userAgent: navigator.userAgent,
      },
    });
  }, []);

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      <h2 className="text-lg font-semibold">{t("errors.loadTimeout.title")}</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        {t("errors.loadTimeout.description")}
      </p>
      <Button onClick={() => window.location.reload()}>
        {t("errors.loadTimeout.retry")}
      </Button>
    </div>
  );
}
